'use client';

import { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { mockBills } from './types';
import type { BillsContextType, Bill, BillFormData, BillFrequency } from './types';

const BillsContext = createContext<BillsContextType | undefined>(undefined);

export function BillsProvider({ children }: { children: React.ReactNode }) {
  const [bills, setBills] = useState<Bill[]>(mockBills);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const addBill = useCallback(async (billData: BillFormData): Promise<Bill> => {
    setProcessing(true);
    return new Promise((resolve) => {
      setTimeout(() => {
        const newBill: Bill = {
          ...billData,
          id: Math.random().toString(36).substr(2, 9),
          status: 'PENDING',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          nextDueDate: billData.dueDate,
        };
        setBills(prev => [...prev, newBill]);
        setProcessing(false);
        resolve(newBill);
      }, 500);
    });
  }, []);

  const updateBill = useCallback(async (id: string, updates: Partial<Bill>): Promise<Bill | undefined> => {
    setProcessing(true);
    return new Promise((resolve) => {
      setTimeout(() => {
        setBills(prev => {
          const updatedBills = prev.map(bill => 
            bill.id === id 
              ? { ...bill, ...updates, updatedAt: new Date().toISOString() } 
              : bill
          );
          return updatedBills;
        });
        setProcessing(false);
        const updatedBill = bills.find(bill => bill.id === id);
        resolve(updatedBill);
      }, 500);
    });
  }, [bills]);

  const deleteBill = useCallback(async (id: string): Promise<void> => {
    setProcessing(true);
    return new Promise((resolve) => {
      setTimeout(() => {
        setBills(prev => prev.filter(bill => bill.id !== id));
        setProcessing(false);
        resolve();
      }, 500);
    });
  }, []);

  const processBillPayment = useCallback(async (id: string): Promise<Bill> => {
    return updateBill(id, { status: 'PAID' }) as Promise<Bill>;
  }, [updateBill]);

  const toggleAutoPay = useCallback(async (id: string, autoPay: boolean): Promise<Bill> => {
    return updateBill(id, { autoPay }) as Promise<Bill>;
  }, [updateBill]);

  const getUpcomingBills = useCallback((): Bill[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bills.filter(bill => {
      const dueDate = new Date(bill.dueDate);
      return dueDate >= today && bill.status === 'PENDING';
    });
  }, [bills]);

  const getOverdueBills = useCallback((): Bill[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bills.filter(bill => {
      const dueDate = new Date(bill.dueDate);
      return dueDate < today && bill.status === 'PENDING';
    });
  }, [bills]);

  const getPaidBills = useCallback((): Bill[] => {
    return bills.filter(bill => bill.status === 'PAID');
  }, [bills]);

  const getBillStats = useCallback(() => {
    const upcomingBills = getUpcomingBills();
    const overdueBills = getOverdueBills();
    
    return {
      totalBills: bills.length,
      totalAmount: bills.reduce((sum, bill) => sum + bill.amount, 0),
      upcomingCount: upcomingBills.length,
      overdueCount: overdueBills.length,
    };
  }, [bills, getUpcomingBills, getOverdueBills]);

  const value = useMemo(() => ({
    bills,
    loading,
    processing,
    addBill,
    updateBill,
    deleteBill,
    processBillPayment,
    toggleAutoPay,
    getUpcomingBills,
    getOverdueBills,
    getPaidBills,
    getBillStats,
  }), [
    bills, 
    loading, 
    processing, 
    addBill, 
    updateBill, 
    deleteBill, 
    processBillPayment, 
    toggleAutoPay,
    getUpcomingBills,
    getOverdueBills,
    getPaidBills,
    getBillStats,
  ]);

  return (
    <BillsContext.Provider value={value}>
      {children}
    </BillsContext.Provider>
  );
}

export function useBills() {
  const context = useContext(BillsContext);
  if (context === undefined) {
    throw new Error('useBills must be used within a BillsProvider');
  }
  return context;
}
